import React, { useEffect, useCallback, useRef, memo, Fragment } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';

import { useSoyuzLocales } from '@spotahome/soyuz/client';

import { InstanceManager, EcommerceManagerFactory } from '@common/tracking';

import './Favorites.scss';
import EmptyFavoritesLogged from './EmptyFavoritesLogged';
import EmptyFavoritesNotLogged from './EmptyFavoritesNotLogged';
import FavoriteMessageLogged from './FavoriteMessageLogged';
import FavoriteMessageNotLogged from './FavoriteMessageNotLogged';
import FavoritesHomecard from './FavoritesHomecard';

const renderFavoriteMessage = isLogged =>
  isLogged ? <FavoriteMessageLogged /> : <FavoriteMessageNotLogged />;

const renderEmptyContent = isLogged =>
  isLogged ? <EmptyFavoritesLogged /> : <EmptyFavoritesNotLogged />;

const Favorites = memo(({ homecards, isLogged, onToggleFavorite }) => {
  const ecommerceManager = useRef();

  const prevHomecards = useRef(homecards);

  const { current } = useSoyuzLocales();

  useEffect(() => {
    ecommerceManager.current = EcommerceManagerFactory(
      InstanceManager.getClientSideInstance()
    );
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    try {
      if (!isEqual(prevHomecards.current, homecards)) {
        ecommerceManager.current.trackFavoritesImpressions(homecards, current);
        prevHomecards.current = homecards; // update homecards reference
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to track impressions at Favorites', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homecards]); // locales doesn't change, should be part of the Instance initialization eventually

  const handleHomecardClick = useCallback(
    (homecard, position) => {
      ecommerceManager.current.trackFavoriteClick(homecard, current, position);
    },
    [current]
  );

  return (
    <div className="l-results--favorites">
      <Fragment>
        {homecards.length ? (
          <>
            {renderFavoriteMessage(isLogged)}
            <div className="l-favorites-card-wrapper">
              {homecards.map((homecard, position) => (
                <FavoritesHomecard
                  key={homecard.id}
                  homecard={homecard}
                  onToggleFavorite={onToggleFavorite}
                  onClick={() => handleHomecardClick(homecard, position)}
                />
              ))}
            </div>
          </>
        ) : (
          renderEmptyContent(isLogged)
        )}
      </Fragment>
    </div>
  );
});

Favorites.propTypes = {
  homecards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      currencyLabel: PropTypes.string,
      currencySymbol: PropTypes.string,
      currencyIsoCode: PropTypes.string
    })
  ),
  isLogged: PropTypes.bool,
  onToggleFavorite: PropTypes.func.isRequired
};

Favorites.defaultProps = {
  homecards: [],
  isLogged: null
};

export default Favorites;
